import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { TIERS } from "@/lib/constants";
import { RISK_LEVELS } from "@/lib/risk";
import ExportButton from "@/components/ExportButton";
import { ACTIVITY_COLUMNS } from "@/lib/exportColumns";
import api from "@/lib/api";

const TYPE_FILTERS = [
  { value: "all", label: "All types" },
  { value: "picked_up", label: "Picked up" },
  { value: "no_answer", label: "Didn't pick up" },
  { value: "internal_call", label: "Internal call" },
  { value: "trial_call", label: "Trial call" },
  { value: "note", label: "Note" },
  { value: "general_note", label: "General note" },
  { value: "trial_note", label: "Trial note" },
];

const CATEGORY_FILTERS = [
  { value: "all", label: "All categories" },
  { value: "lu", label: "LU call" },
  { value: "support", label: "Support call" },
  { value: "other", label: "Other" },
];

/**
 * ActivityFilters — the dropdown row + date pickers + search + Export button
 * used at the top of the Activity page. Extracted out of Activity.jsx so the
 * page itself stays under 500 LOC and so this UI can be reused if we ever
 * embed the same filter set inside a Firm 360 tab.
 *
 * Every value/handler is passed in by the parent; the component is stateless.
 * `pageReset` is invoked once whenever any filter changes so the table jumps
 * back to page 1 — same wiring the old inline code had.
 */
export default function ActivityFilters({
  // values
  dateFrom, dateTo, filterPoc, filterLoggedBy, filterType, filterTier,
  filterRisk, filterCategory, filterFirm,
  // setters
  setDateFrom, setDateTo, setFilterPoc, setFilterLoggedBy, setFilterType,
  setFilterTier, setFilterRisk, setFilterCategory, setFilterFirm,
  // options + flags
  pocOptions, loggerOptions, isPoc,
  filtersDirty, clearFilters, pageReset, total,
}) {
  // Wrap each setter so we also reset the table to page 1.
  const onChange = (setter) => (v) => { setter(v); pageReset(); };

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2 shrink-0"
      data-testid="act-filter-bar"
    >
      <Input type="date" value={dateFrom} onChange={(e) => onChange(setDateFrom)(e.target.value)} placeholder="From" className="w-[140px] h-9 text-xs" data-testid="act-filter-from" />
      <Input type="date" value={dateTo} onChange={(e) => onChange(setDateTo)(e.target.value)} placeholder="To" className="w-[140px] h-9 text-xs" data-testid="act-filter-to" />

      {/* POC dropdown is hidden for single-assignment POCs — nothing to choose */}
      {(!isPoc || pocOptions.length > 1) && (
        <Select value={filterPoc} onValueChange={onChange(setFilterPoc)}>
          <SelectTrigger className="w-[120px] h-9 text-xs" data-testid="act-filter-poc"><SelectValue placeholder="POC" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All POCs</SelectItem>
            {pocOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      <Select value={filterLoggedBy} onValueChange={onChange(setFilterLoggedBy)}>
        <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="act-filter-loggedby"><SelectValue placeholder="Logged by" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All loggers</SelectItem>
          {loggerOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={filterType} onValueChange={onChange(setFilterType)}>
        <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="act-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
        <SelectContent>
          {TYPE_FILTERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={filterTier} onValueChange={onChange(setFilterTier)}>
        <SelectTrigger className="w-[110px] h-9 text-xs" data-testid="act-filter-tier"><SelectValue placeholder="Tier" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All tiers</SelectItem>
          {TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={filterRisk} onValueChange={onChange(setFilterRisk)}>
        <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="act-filter-risk"><SelectValue placeholder="Risk" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All risk</SelectItem>
          {RISK_LEVELS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={filterCategory} onValueChange={onChange(setFilterCategory)}>
        <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="act-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
        <SelectContent>
          {CATEGORY_FILTERS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
        </SelectContent>
      </Select>

      {/* Search lives on its own line below to give it a wider input */}
      <div className="basis-full" />

      <div className="relative">
        <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          placeholder="Search firm…"
          value={filterFirm}
          onChange={(e) => onChange(setFilterFirm)(e.target.value)}
          className="w-[300px] h-9 pl-7 text-xs"
          data-testid="act-filter-firm"
        />
      </div>

      {filtersDirty && (
        <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-gray-500 h-9" data-testid="act-clear-filters">
          <X size={12} className="mr-1" /> Clear
        </Button>
      )}

      <div className="ml-auto flex items-center gap-3">
        <span className="text-[11px] text-gray-500 font-mono">
          {total ?? 0} entr{total === 1 ? "y" : "ies"}
        </span>
        <ExportButton
          filename="activity"
          sheetName="Activity"
          columns={ACTIVITY_COLUMNS}
          fetchRows={async () => {
            const res = await api.get("/activity", {
              params: {
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                poc: filterPoc !== "all" ? filterPoc : undefined,
                firm: filterFirm || undefined,
                type: filterType !== "all" ? filterType : undefined,
                tier: filterTier !== "all" ? filterTier : undefined,
                risk: filterRisk !== "all" ? filterRisk : undefined,
                category: filterCategory !== "all" ? filterCategory : undefined,
                logged_by: filterLoggedBy !== "all" ? filterLoggedBy : undefined,
                page: 1,
                page_size: 10000,
              },
            });
            return res.data.rows || [];
          }}
          testId="act-export-xlsx-btn"
        />
      </div>
    </div>
  );
}
